import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { type Registry as Services, service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import { isBefore } from 'date-fns/isBefore';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { BookingChannel } from 'uplisting-frontend/models/booking';
import { nightsBetween } from 'uplisting-frontend/utils/date';
import { getPreferredMessagingChannel } from 'uplisting-frontend/utils/messaging-channels';

export default class EnquiryModel extends Model {
  @service translations!: Services['translations'];

  @attr('string') checkIn!: string | null;
  @attr('string') checkOut!: string | null;
  @attr('string') guestName!: string;
  @attr('number') numberOfGuests!: number;
  @attr('string') status!: string;
  @attr('string') channel!: BookingChannel;

  @belongsTo('calendar', { inverse: null, async: false })
  calendar!: ModelRegistry['calendar'];

  @belongsTo('property', { inverse: null, async: false })
  property!: ModelRegistry['property'];

  @belongsTo('inbox-message', { inverse: 'enquiries', async: false })
  inboxMessage!: ModelRegistry['inbox-message'];

  @belongsTo('channel-user', { async: false, inverse: null })
  channelUser!: ModelRegistry['channel-user'];

  @belongsTo('ai-occasion-context', { inverse: null, async: false })
  aiOccasionContext?: ModelRegistry['ai-occasion-context'];

  @belongsTo('multi-unit', {
    inverse: 'enquiries',
    async: false,
  })
  multiUnit!: ModelRegistry['multi-unit'];

  @hasMany('messaging-channel', { inverse: 'enquiry', async: false })
  messagingChannels!: ModelRegistry['messaging-channel'][];

  @cached
  get preferredMessagingChannel(): string {
    return getPreferredMessagingChannel(this.channel, this.messagingChannels);
  }

  @hasMany('message', { async: false, inverse: 'enquiry' })
  messages!: ModelRegistry['message'][];

  @cached
  get isAirbnbOfficial(): boolean {
    return this.channel === BookingChannel.airbnbOfficial;
  }

  @cached
  get isUplisting(): boolean {
    return this.channel === BookingChannel.uplisting;
  }

  @cached
  get isVrbo(): boolean {
    return this.channel === BookingChannel.homeAway;
  }

  @cached
  get isBookingDotCom(): boolean {
    return this.channel === BookingChannel.bookingDotCom;
  }

  @cached
  get isPending(): boolean {
    if (!this.isAirbnbOfficial) {
      return false;
    }

    return this.status === 'active';
  }

  @cached
  get isAccepted(): boolean {
    if (!this.isAirbnbOfficial) {
      return false;
    }

    return ['booked', 'accepted'].includes(this.status);
  }

  @cached
  get isSpam(): boolean {
    return this.status === 'spam';
  }

  @cached
  get isDateless(): boolean {
    return !this.checkIn || !this.checkOut;
  }

  @cached
  get displayChannel(): string {
    if (this.channel) {
      return this.translations.getChannelNameFor(this.channel);
    }

    return this.channel;
  }

  @cached
  get duration(): number {
    return nightsBetween(this.checkIn, this.checkOut);
  }

  @cached
  get messagesSorted(): ModelRegistry['message'][] {
    return this.messages.slice().sort((x, y) => {
      if (isBefore(x.createdAt, y.createdAt)) {
        return -1;
      } else if (isBefore(y.createdAt, x.createdAt)) {
        return 1;
      }

      return 0;
    });
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    enquiry: EnquiryModel;
  }
}
